/**
 * useNotifications Hook
 *
 * React hook for subscribing to real-time notifications via WebSocket.
 * Automatically connects when user is authenticated and disconnects on logout.
 *
 * Features:
 * - Auto-connect on authentication
 * - Real-time notification updates
 * - Unread count tracking
 * - Toast notifications for new messages
 */

'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '../lib/store/auth-store';
import {
  notificationSocketService,
  type NewNotificationPayload,
  type ConnectionStatus,
} from '../services/notification-socket.service';

interface UseNotificationsOptions {
  showToasts?: boolean;
  onNewNotification?: (notification: NewNotificationPayload) => void;
}

interface UseNotificationsReturn {
  unreadCount: number;
  isConnected: boolean;
  connectionStatus: ConnectionStatus;
  markAsRead: (notificationId: string) => void;
}

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const { showToasts = true, onNewNotification } = options;
  const t = useTranslations('myOrders');
  const router = useRouter();

  const { isAuthenticated } = useAuthStore();
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const cleanupRef = useRef<(() => void)[]>([]);

  // Use ref to avoid recreating callbacks on every render
  const optionsRef = useRef({ showToasts, onNewNotification, t, router });
  useEffect(() => {
    optionsRef.current = { showToasts, onNewNotification, t, router };
  });

  // Connect/disconnect based on authentication
  useEffect(() => {
    if (isAuthenticated) {
      // Connect is now async, but we don't need to await
      notificationSocketService.connect().catch((error) => {
        console.error('[useNotifications] Failed to connect:', error);
      });
    } else {
      notificationSocketService.disconnect();
      setUnreadCount(0);
    }

    return () => {
      // Don't disconnect on unmount if still authenticated
      // Let the service handle reconnection
    };
  }, [isAuthenticated]);

  // Subscribe to events
  useEffect(() => {
    if (!isAuthenticated) return;

    console.log('[useNotifications] Subscribing to events, showToasts:', options.showToasts);

    // Connection status
    const unsubConnect = notificationSocketService.on('connect', () => {
      setConnectionStatus('connected');
    });

    const unsubDisconnect = notificationSocketService.on('disconnect', () => {
      setConnectionStatus('disconnected');
    });

    // Unread count
    const unsubUnreadCount = notificationSocketService.on('notification:unread-count', (data) => {
      setUnreadCount(data.count);
    });

    // New notification
    const unsubNewNotification = notificationSocketService.on('notification:new', (notification) => {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('[useNotifications] 📨 Received notification:new event');
      console.log('[useNotifications] Type:', notification.type);
      console.log('[useNotifications] Title:', notification.title);
      console.log('[useNotifications] showToasts setting:', optionsRef.current.showToasts);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // Update unread count
      setUnreadCount((prev) => prev + 1);

      // Get current options from ref
      const { showToasts: currentShowToasts, onNewNotification: currentCallback, t: currentT, router: currentRouter } = optionsRef.current;

      // Callback
      currentCallback?.(notification);

      // Show toast
      if (currentShowToasts) {
        console.log('[useNotifications] 🔍 Checking if should show toast...');

        // Only show toast for CRITICAL SYSTEM notifications
        // ALL order-related, payment, dispute notifications appear in the notification bell only
        const toastEnabledTypes = [
          'SECURITY_ALERT',    // Critical: Security issues require immediate attention
        ];

        const shouldShowToast = toastEnabledTypes.includes(notification.type);
        console.log('[useNotifications] Is in toastEnabledTypes?', shouldShowToast);
        console.log('[useNotifications] Allowed types:', toastEnabledTypes);

        if (shouldShowToast) {
          console.log('[useNotifications] ✅ SHOWING TOAST for:', notification.type);

          // Critical system notification toast
          toast(notification.title, {
            description: notification.body,
            action: notification.actionUrl
              ? {
                  label: 'View',
                  onClick: () => currentRouter.push(notification.actionUrl!),
                }
              : undefined,
            duration: 8000, // Longer duration for critical notifications
          });
        } else {
          console.log('[useNotifications] ❌ SKIPPING TOAST - Only showing in bell for:', notification.type);
        }
      } else {
        console.log('[useNotifications] ⏭️ showToasts is FALSE - skipping toast logic');
      }
    });

    // Cleanup function - MUST be called to avoid memory leaks in Strict Mode
    return () => {
      console.log('[useNotifications] Cleaning up subscriptions, showToasts was:', optionsRef.current.showToasts);
      // Call all unsubscribe functions immediately
      unsubConnect();
      unsubDisconnect();
      unsubUnreadCount();
      unsubNewNotification();
    };
  }, [isAuthenticated]); // Removed showToasts, onNewNotification, t, router from dependencies

  const markAsRead = useCallback((notificationId: string) => {
    notificationSocketService.markAsRead(notificationId);
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  return {
    unreadCount,
    isConnected: connectionStatus === 'connected',
    connectionStatus,
    markAsRead,
  };
}
